import { useEffect, useMemo, useState } from "react";
import { formatMoney } from "../../../../utils";
import { fetchSettings } from "../../service";
import { getSettings as getLocalSettings } from "../../../../storage";
import { theme } from "../../../../theme";
import PriceBreakdown from "./PriceBreakdown";

const FixedCharges = ({
    isHome,
    isCurrentJob = false,
    isShowOther = false,
    iconColor = "",
    hourlyRate = null,
    isWithIcon = true,
}) => {
    const flowSettings = getLocalSettings();
    const stepFlowIsEnable = flowSettings?.customer_web_job_step_flow_is_enable;

    const [settings, setSettings] = useState(null);
    const [showPriceBreakdown, setShowPriceBreakdown] = useState(false);

    const togglePriceBreakdown = () => {
        setShowPriceBreakdown((prev) => !prev);
    }

    useEffect(() => {
        let mounted = true;

        const loadSettings = async () => {
            try {
                const response = await fetchSettings();

                if (mounted && response?.status === "Success") {
                    setSettings(response.settings);
                }
            } catch (error) {
                console.error("Failed to fetch settings:", error);
            }
        };

        loadSettings();

        return () => {
            mounted = false;
        };
    }, []);

    const fieldsToShow = useMemo(() => {
        return [
            ...(hourlyRate
                ? [
                    {
                        label: "Hourly rate",
                        field: "hourly_rate_charge",
                        icon: "access_time",
                    },
                ]
                : []),

            {
                label: "Booking fee",
                field: "job_booking_fee",
                icon: "assignment",
            },

            {
                label: "Call-out fee",
                field: "job_call_out_fee",
                icon: "local_shipping",
            },

            ...(isShowOther
                ? [
                    // {
                    //     label: "VAT",
                    //     field: "fix_charge_vat",
                    //     icon: "percent_discount",
                    // },
                    {
                        label: "Sub total",
                        field: "total_fix_charge_with_vat",
                        icon: "calculate",
                    },
                ]
                : []),
        ];
    }, [hourlyRate, isShowOther]);

    const renderLabel = (label) => {
        if (label === "VAT" && settings?.company_vat) {
            return `${label} (${settings.company_vat}%)`;
        }

        return label;
    };

    const renderRow = (item, index, compact = false) => {
        const value =
            item.field === "hourly_rate_charge"
                ? hourlyRate
                : settings?.[item.field];

        if (!value) return null;

        if (compact) {
            return (
                <div
                    key={index}
                    className="d-flex justify-content-between align-items-center p-2"
                >
                    <div className="d-flex align-items-center">
                        {isWithIcon && (
                            <i
                                className={`material-symbols-rounded me-2 ${iconColor}`}
                            >
                                {item.icon}
                            </i>
                        )}

                        <span>{renderLabel(item.label)}</span>
                    </div>

                    <span className="fw-bold">
                        + {formatMoney(value)}
                    </span>
                </div>
            );
        }

        return (
            <div className="row mt-2" key={index}>
                <div className="col-7 text-start">
                    <div className="d-flex align-items-center text-sm font-weight-bold">
                        {isWithIcon && (
                            <i
                                className={`material-symbols-rounded me-2 ${iconColor}`}
                            >
                                {item.icon}
                            </i>
                        )}

                        <span className="text-sm">
                            {renderLabel(item.label)}
                        </span>
                    </div>
                </div>

                <div className="col-5 text-end">
                    <p className="font-weight-bolder mb-0 text-sm">
                        {formatMoney(value)}
                    </p>
                </div>
            </div>
        );
    };

    if (!settings) return <p className="text-center mt-4">Loading...</p>;

    if (!isCurrentJob && stepFlowIsEnable) {
        const filteredFields = fieldsToShow.filter(
            (item) => item.label !== "Sub total" && item.label !== "Hourly rate"
        );

        const subTotal = settings?.total_fix_charge_with_vat;

        return (
            <div className="card job-card-bg-color-hash">
                <div className="card-body">
                    <h6 className="mb-0 text-md">Pay Now</h6>

                    <p className="text-xs">
                        We only charge {formatMoney(subTotal ?? 0)} today.
                        Time on site is charged after the visit.
                    </p>

                    <div className="pe-2">
                        {filteredFields.map((item, index) =>
                            renderRow(item, index)
                        )}
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <div className="d-flex align-items-center">
                            <h6 className="mb-0 text-md">Total taken today</h6>
                        </div>

                        <h6 className="mb-0 text-md pe-2">
                            {formatMoney(subTotal)}
                        </h6>
                    </div>

                    <p className="text-xs">
                        This secures your booking and covers travel to your property
                    </p>

                    <button
                            className={`btn btn-link text-${theme.main} text-md ps-0 my-0 py-0`}
                            type="button"
                            onClick={togglePriceBreakdown}
                        >
                            Price breakdown
                    </button>
                </div>

                <PriceBreakdown
                    show={showPriceBreakdown}
                    handleClose={togglePriceBreakdown}
                />
            </div>
        );
    }

    const compactView = !(isHome || isCurrentJob);

    return (
        <>
            {fieldsToShow.map((item, index) =>
                renderRow(item, index, compactView)
            )}
        </>
    );
};

export default FixedCharges;